import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Search } from "lucide-react";

import {
  COUNTRIES,
  sortedCountries,
  findCountry,
  parsePhone,
  joinPhone,
} from "../../i18n/countries";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

/**
 * PhoneInput — controlled: value is an E.164-ish string ("+491701234567").
 *
 * Props:
 *   value:       string   — E.164 form (or empty)
 *   onChange:    (v)=>void
 *   onBlur:      ()=>void (optional)
 *   disabled:    bool
 *   error:       bool
 *   testIdRoot:  string   — used to build data-testids for both parts
 */
export function PhoneInput({
  value,
  onChange,
  onBlur,
  disabled,
  error,
  testIdRoot = "phone",
  placeholder,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith("en") ? "en" : "de";

  const parsed = useMemo(() => parsePhone(value || ""), [value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const country = findCountry(parsed.iso2);
  const list = useMemo(() => {
    const all = sortedCountries(lng);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => {
      const name = (lng === "en" ? c.name_en : c.name_de).toLowerCase();
      return (
        name.includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.dial_code.includes(q.replace(/^\+/, ""))
      );
    });
  }, [lng, query]);

  const setCountry = (iso2) => {
    onChange(joinPhone(iso2, parsed.national));
    setOpen(false);
    setQuery("");
  };

  const setNational = (raw) => {
    // Keep only digits + spaces for display; store digits-only.
    onChange(joinPhone(parsed.iso2, raw));
  };

  const errRing = error
    ? "!ring-1 !ring-status-unavailable !border-status-unavailable"
    : "";

  return (
    <div className="flex items-stretch gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testIdRoot}-country-trigger`}
            className={`inline-flex h-10 min-w-[110px] items-center gap-1.5 rounded-md border border-border-subtle bg-surface px-2.5 text-sm text-text-primary transition-colors hover:bg-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne disabled:opacity-60 ${errRing}`}
          >
            <span aria-hidden className="text-base leading-none">
              {country.flag}
            </span>
            <span className="font-mono text-xs text-text-secondary">
              +{country.dial_code}
            </span>
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-text-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          data-testid={`${testIdRoot}-country-content`}
          className="w-80 border-border-subtle bg-surface p-0"
        >
          <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
            <Search className="h-4 w-4 text-text-secondary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("forms.country_search_placeholder")}
              autoFocus
              data-testid={`${testIdRoot}-country-search`}
              className="w-full bg-transparent text-sm outline-none placeholder:text-text-secondary"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {list.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-text-secondary">
                {t("forms.country_no_results")}
              </div>
            ) : (
              list.map((c) => {
                const active = c.iso2 === parsed.iso2;
                const name = lng === "en" ? c.name_en : c.name_de;
                return (
                  <button
                    key={c.iso2}
                    type="button"
                    onClick={() => setCountry(c.iso2)}
                    data-testid={`${testIdRoot}-country-option-${c.iso2}`}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-offwhite ${
                      active ? "bg-offwhite" : ""
                    }`}
                  >
                    <span aria-hidden className="text-base leading-none">
                      {c.flag}
                    </span>
                    <span className="flex-1 truncate">{name}</span>
                    <span className="font-mono text-xs text-text-secondary">
                      +{c.dial_code}
                    </span>
                    {active && <Check className="h-4 w-4 text-champagne" />}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Input
        type="tel"
        inputMode="tel"
        value={parsed.national}
        onChange={(e) => setNational(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder || "170 1234567"}
        data-testid={`${testIdRoot}-number`}
        className={`h-10 flex-1 bg-surface border-border-subtle focus-visible:ring-champagne ${errRing}`}
      />
    </div>
  );
}

// Sanity: keep this in-file so a bad refactor of countries.js is caught at build.
export const _phoneCountryCount = COUNTRIES.length;
